import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BadgeCheck, Quote, LogIn, AlertCircle } from 'lucide-react'

import LoadingSpinner from '../components/LoadingSpinner'
import { Button } from '@/components/ui/button'
import api from '../services/api'
import { getToken } from '../utils/userData'
import { trackEvent } from '../utils/mixpanel'

const ERROR_COPY = {
  not_found: {
    title: 'Invalid link',
    body: 'This testimonial link doesn’t exist. Please double-check the link you were sent.',
  },
  used: {
    title: 'Link already used',
    body: 'A testimonial has already been submitted with this link.',
  },
  expired: {
    title: 'Link expired',
    body: 'This testimonial link has expired. Ask the facilitator to send you a fresh one.',
  },
}

const TestimonialSubmit = () => {
  const { token } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [validation, setValidation] = useState(null) // { valid, reason, facilitatorName }
  const [form, setForm] = useState({ quote: '', authorRole: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [success, setSuccess] = useState(false)

  const isLoggedIn = Boolean(getToken())

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Testimonial Submit' })
  }, [])

  useEffect(() => {
    const validate = async () => {
      try {
        setLoading(true)
        const response = await api.get(`/api/testimonials/${token}`)
        setValidation(response.data.data)
      } catch (err) {
        setValidation(err.response?.data?.data || { valid: false, reason: 'not_found' })
      } finally {
        setLoading(false)
      }
    }
    if (token) validate()
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.quote.trim()) return
    try {
      setSubmitting(true)
      setSubmitError(null)
      await api.post(`/api/testimonials/${token}`, form)
      trackEvent('Testimonial Submitted', { facilitator: validation?.facilitatorName })
      setSuccess(true)
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-cream to-primary-100 px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-primary-100/70 bg-white/90 p-6 shadow-md backdrop-blur-sm sm:p-8"
      >
        {/* Invalid token */}
        {!validation?.valid ? (
          <div className="text-center">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">
              {(ERROR_COPY[validation?.reason] || ERROR_COPY.not_found).title}
            </h1>
            <p className="mb-6 text-sm text-gray-600">
              {(ERROR_COPY[validation?.reason] || ERROR_COPY.not_found).body}
            </p>
            <Button asChild className="rounded-full bg-primary-700 hover:bg-primary-800">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        ) : success ? (
          <div className="text-center">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <BadgeCheck className="h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">Thank you!</h1>
            <p className="mb-6 text-sm text-gray-600">
              Your testimonial for {validation.facilitatorName || 'the facilitator'} has been submitted.
            </p>
            <Button asChild className="rounded-full bg-primary-700 hover:bg-primary-800">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        ) : !isLoggedIn ? (
          <div className="text-center">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <Quote className="h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">
              Share your experience{validation.facilitatorName ? ` with ${validation.facilitatorName}` : ''}
            </h1>
            <p className="mb-6 text-sm text-gray-600">
              Please sign in or create an account to verify your testimonial. It only takes a moment.
            </p>
            <Button
              onClick={() => navigate(`/login?redirect=/testimonial/${token}`)}
              className="rounded-full bg-primary-700 hover:bg-primary-800"
            >
              <LogIn className="h-4 w-4" />
              Sign in to continue
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-5 text-center">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <Quote className="h-6 w-6" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                Share your experience{validation.facilitatorName ? ` with ${validation.facilitatorName}` : ''}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                Your words will appear on their public profile under your name.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Your testimonial <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.quote}
                  onChange={(e) => setForm((prev) => ({ ...prev, quote: e.target.value }))}
                  className="w-full resize-none rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="What was your experience like?"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Your role / description
                </label>
                <input
                  type="text"
                  value={form.authorRole}
                  onChange={(e) => setForm((prev) => ({ ...prev, authorRole: e.target.value }))}
                  className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="e.g. Designer · Bengaluru"
                />
              </div>

              {submitError && <p className="text-xs text-red-600">{submitError}</p>}

              <Button
                type="submit"
                disabled={submitting}
                className="h-11 w-full rounded-full bg-primary-700 text-sm font-semibold hover:bg-primary-800 disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit testimonial'}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  )
}

export default TestimonialSubmit
